import { StrictMode, useEffect, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router';
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import {
  FluentProvider,
  Spinner,
  webLightTheme,
} from '@fluentui/react-components';
import { MsalProvider } from '@azure/msal-react';
import App from './routes/home/Home';
import {
  Containers,
  loader as containersLoader,
} from './routes/containers/Containers';
import { ContainerBrowser } from './components/container-browser/ContainerBrowser';
import ErrorBoundary from './routes/error/ErrorBoundary';
import ErrorPage from './routes/error/ErrorPage';
import { msalInstance, initializeMsalInstance } from './providers/msalProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { hasAuthenticationParams } from './utils/MsalUtils';
import { clarityService } from './utils/ClarityService';
import './styles/Main.css';

// Register icons and pull the fonts from the default Microsoft Fluent CDN:
initializeFileTypeIcons();
initializeIcons();

const AppWrapper = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const initializeApp = async () => {
      try {
        clarityService.init();

        if (hasAuthenticationParams()) {
          console.log(
            'Authentication parameters detected, handling redirect...',
          );
        }

        try {
          if (globalThis.parent.length > 0) {
            console.log('App is running inside Microsoft Teams.');
            // Teams context is ready immediately
            setIsInitialized(true);
          } else {
            console.log('App is not running inside Microsoft Teams.');
            console.log(
              'Initializing MSAL instance...',
              globalThis.location.origin,
            );

            // Initialize MSAL instance
            await initializeMsalInstance();

            setIsInitialized(true);
          }
        } catch (error) {
          console.error('Error initializing Teams context:', error);
          console.log(
            'Initializing MSAL instance...',
            globalThis.location.origin,
          );

          // Initialize MSAL instance as fallback
          await initializeMsalInstance();

          // Add extra settling time for MSAL
          await new Promise((resolve) => setTimeout(resolve, 500));

          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        setInitializationError(
          error instanceof Error
            ? error.message
            : 'Unknown initialization error',
        );
      }
    };

    initializeApp();
  }, []);

  // Created once, after MSAL is ready. Creating it on every render re-ran the route
  // loaders (and could trigger duplicate token acquisition) on each state change.
  const router = useMemo(
    () => (isInitialized ? createAppRouter() : null),
    [isInitialized],
  );

  const renderContent = () => {
    if (initializationError) {
      return (
        <div className="app-overlay app-error-overlay">
          <div className="app-error-content">
            <h3>Application failed to initialize</h3>
            <p>{initializationError}</p>
            <button onClick={() => globalThis.location.reload()}>Retry</button>
          </div>
        </div>
      );
    }

    if (!isInitialized || !router) {
      return (
        <div className="app-overlay">
          <Spinner size="large" label="Initializing application..." />
        </div>
      );
    }

    return (
      <ErrorBoundary>
        <MsalProvider instance={msalInstance as any}>
          <RouterProvider
            router={router}
          />
        </MsalProvider>
      </ErrorBoundary>
    );
  };

  return (
    <StrictMode>
      <FluentProvider theme={webLightTheme} className="kt-ui-app">
        {renderContent()}
      </FluentProvider>
    </StrictMode>
  );
};

const RouteLoadingFallback = () => (
  <div className="app-overlay">
    <Spinner size="large" label="Loading..." />
  </div>
);

const createAppRouter = () =>
  createBrowserRouter([
    {
      path: '/',
      element: <ProtectedRoute />,
      HydrateFallback: RouteLoadingFallback,
      errorElement: <ErrorPage />,
      children: [
        {
          path: '/',
          element: <App />,
          children: [
            {
              index: true,
              element: <Navigate to="/containers" replace />,
            },
            {
              path: 'containers',
              loader: containersLoader,
              element: <Containers />,
            },
            {
              path: 'containers/:containerId',
              loader: containersLoader,
              element: <ContainerBrowser />,
            },
          ],
        },
      ],
    },
  ]);

const rootElement = document.getElementById('root');
const root = rootElement ? ReactDOM.createRoot(rootElement) : null;

if (root) {
  root.render(<AppWrapper />);
} else {
  console.error('Root element not found');
}

export default AppWrapper;
